import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  EmptyState,
  KPITile,
  Skeleton,
  StatusPill,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { formatGBP, formatDate } from "../lib/format.js";
import { useToast } from "../lib/toast.js";
import type {
  DashboardTodayResponse,
  AdminApplicationSummary,
  AdminOrderSummaryDTO,
  AdminOrderListResponse,
  OrderStatus,
} from "@wmart/shared-types";

/**
 * Compute today's local-midnight as a UTC ISO datetime string for use in the
 * `placed_after` query param of `/v1/admin/orders`. The Zod schema expects
 * `z.iso.datetime()` so a non-ISO literal like "today" would 422.
 *
 * Timezone note: this is the BROWSER's local-midnight expressed as UTC, while
 * the dashboard `/today` endpoint defines "today" as Europe/London-midnight.
 * During BST there's a 1h offset. The numbers on the tiles come from the
 * server (Europe/London-aware); this filter is just a navigation convenience.
 */
function todayStartIso(): string {
  return new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
}

function greetingForHour(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

/**
 * Format an ISO timestamp for the "Today's orders" table. Same-day → `HH:MM`;
 * earlier than today → `Yest HH:MM` or `dd MMM HH:MM`.
 */
function formatPlacedAt(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  if (sameDay) return `${hh}:${mm}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate()
  ) {
    return `Yest ${hh}:${mm}`;
  }
  return `${String(d.getDate()).padStart(2, "0")} ${d.toLocaleString("en-GB", { month: "short" })} ${hh}:${mm}`;
}

const ORDER_STATUS_PILL: Record<OrderStatus, { tone: "success" | "warning" | "danger" | "info" | "neutral"; label: string }> = {
  pending_payment: { tone: "warning", label: "Awaiting payment" },
  placed: { tone: "info", label: "Placed" },
  // P17 — COD interstitial; render as info-toned "Out for delivery" so
  // dashboard tiles linking into the orders queue list it explicitly.
  out_for_delivery: { tone: "info", label: "Out for delivery" },
  paid: { tone: "success", label: "Paid" },
  fulfilled: { tone: "info", label: "Shipped" },
  cancelled: { tone: "neutral", label: "Cancelled" },
  partially_refunded: { tone: "warning", label: "Partial refund" },
  refunded: { tone: "danger", label: "Refunded" },
};

const BUSINESS_TYPE_LABEL: Record<string, string> = {
  corner_shop: "Corner shop",
  restaurant: "Restaurant",
  cafe: "Café",
  hotel: "Hotel",
  catering: "Catering",
  bakery: "Bakery",
  wholesale_reseller: "Wholesale reseller",
  other: "Other",
};

type Range = 7 | 30;

interface DashboardData {
  dashboard: DashboardTodayResponse;
  todayOrders: AdminOrderSummaryDTO[];
  pendingApplications: AdminApplicationSummary[];
}

/**
 * Fetch three endpoints in parallel. A failure of either lateral endpoint
 * (orders, applications) doesn't block the page — those sections render
 * empty-states. A failure of the main dashboard endpoint surfaces as a toast.
 *
 * The lateral fetchers tolerate both rejection AND a malformed/undefined
 * response shape (e.g. when a test mocks `api.get` with a value that lacks
 * an `items` array). They default to `[]` so the page stays renderable.
 */
async function safeOrders(placedAfter: string): Promise<AdminOrderSummaryDTO[]> {
  try {
    const res = await api.get<AdminOrderListResponse>(
      `/v1/admin/orders?placed_after=${encodeURIComponent(placedAfter)}&limit=7`,
    );
    return Array.isArray(res?.items) ? res.items : [];
  } catch {
    return [];
  }
}

async function safePendingApplications(): Promise<AdminApplicationSummary[]> {
  try {
    const res = await api.get<{ items: AdminApplicationSummary[]; next_cursor: string | null }>(
      "/v1/admin/applications?status=pending&limit=3",
    );
    return Array.isArray(res?.items) ? res.items : [];
  } catch {
    return [];
  }
}

async function loadDashboard(range: Range, placedAfter: string): Promise<DashboardData> {
  const [dashboard, todayOrders, pendingApplications] = await Promise.all([
    api.get<DashboardTodayResponse>(`/v1/admin/dashboard/today?range=${range}`),
    safeOrders(placedAfter),
    safePendingApplications(),
  ]);
  return { dashboard, todayOrders, pendingApplications };
}

export function Dashboard() {
  const { notify, notifyProblem } = useToast();
  const [data, setData] = useState<DashboardData | null>(null);
  const [range, setRange] = useState<Range>(7);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await loadDashboard(range, todayStartIso());
        if (!cancelled) setData(res);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
        else notify("error", "Failed to load dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [notify, notifyProblem, range]);

  if (!data) {
    return <DashboardSkeleton />;
  }

  const { dashboard, todayOrders, pendingApplications } = data;
  const placedAfter = encodeURIComponent(todayStartIso());
  const greeting = greetingForHour(new Date().getHours());
  const today = new Date().toISOString();

  // Greeting sub-line summary — only mention the buckets that have work.
  const subSegments: string[] = [];
  if (dashboard.pending_applications_count > 0) {
    subSegments.push(
      `${dashboard.pending_applications_count} application${dashboard.pending_applications_count === 1 ? "" : "s"} awaiting review`,
    );
  }
  if (dashboard.fulfilment_queue > 0) {
    subSegments.push(
      `${dashboard.fulfilment_queue} order${dashboard.fulfilment_queue === 1 ? "" : "s"} to fulfil`,
    );
  }
  if (dashboard.low_stock_count > 0) {
    subSegments.push(
      `${dashboard.low_stock_count} SKU${dashboard.low_stock_count === 1 ? "" : "s"} below stock floor`,
    );
  }
  const subLine = subSegments.length > 0 ? subSegments.join(" · ") + "." : "All caught up.";

  return (
    <div className="flex flex-col gap-8">
      {/* Greeting strip */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif text-display-md text-ink-900">{greeting}</h2>
          <p className="mt-1 text-body-sm text-ink-500">{subLine}</p>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-body-sm text-ink-500">{formatDate(today)}</p>
          <RangeToggle value={range} onChange={setRange} />
        </div>
      </header>

      {/* KPI row */}
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KPITile
          label="Revenue today"
          value={formatGBP(dashboard.revenue_today_minor)}
          spark={dashboard.revenue_series_minor}
          footer={
            <Link
              to={`/orders?status=paid&placed_after=${placedAfter}`}
              className="text-accent hover:underline"
            >
              View today's paid orders →
            </Link>
          }
        />
        <KPITile
          label="Orders today"
          value={dashboard.orders_today}
          footer={
            <Link to={`/orders?placed_after=${placedAfter}`} className="text-accent hover:underline">
              View orders →
            </Link>
          }
        />
        <KPITile
          label="Pending approvals"
          value={dashboard.pending_applications_count}
          footer={
            dashboard.pending_applications_count > 0 ? (
              <Link to="/applications" className="text-accent hover:underline">
                Review →
              </Link>
            ) : (
              "All caught up"
            )
          }
        />
        <KPITile
          label="Awaiting fulfilment"
          value={dashboard.fulfilment_queue}
          footer={
            dashboard.fulfilment_queue > 0 ? (
              <Link to="/orders?status=paid" className="text-accent hover:underline">
                Fulfil →
              </Link>
            ) : (
              "All caught up"
            )
          }
        />
      </section>

      {/* Main grid: today's orders (2/3) + right column (1/3) */}
      <section className="grid gap-6 lg:grid-cols-3">
        <TodayOrdersCard
          orders={todayOrders}
          fulfilmentQueue={dashboard.fulfilment_queue}
          placedAfter={placedAfter}
          className="lg:col-span-2"
        />

        <div className="flex flex-col gap-6">
          <PendingApplicationsCard
            applications={pendingApplications}
            totalPending={dashboard.pending_applications_count}
          />
          <BelowStockFloorCard
            lowStockCount={dashboard.low_stock_count}
            pendingRefundsCount={dashboard.pending_refunds_count}
          />
        </div>
      </section>

    </div>
  );
}

/* ─── Today's orders card ────────────────────────────────────────────── */

function TodayOrdersCard({
  orders,
  fulfilmentQueue,
  placedAfter,
  className,
}: {
  orders: AdminOrderSummaryDTO[];
  fulfilmentQueue: number;
  placedAfter: string;
  className?: string;
}) {
  return (
    <Card as="section" className={`flex flex-col overflow-hidden ${className ?? ""}`}>
      <header className="flex items-center justify-between gap-4 border-b border-ink-200 bg-paper-warm px-6 py-4">
        <div>
          <h3 className="font-serif text-title-md text-ink-900">Today's orders</h3>
          <p className="mt-0.5 text-caption text-ink-500">
            {orders.length} shown · {fulfilmentQueue} still to fulfil
          </p>
        </div>
        <Link
          to={`/orders?placed_after=${placedAfter}`}
          className="text-body-sm font-medium text-accent hover:underline"
        >
          All orders →
        </Link>
      </header>

      {orders.length === 0 ? (
        <div className="p-8">
          <EmptyState
            icon="package"
            title="No orders yet today"
            body="New orders will appear here as they're placed."
          />
        </div>
      ) : (
        <table className="w-full text-body-sm">
          <thead className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
            <tr className="border-b border-ink-200">
              <th className="px-6 py-3 text-left font-semibold">Order</th>
              <th className="px-3 py-3 text-left font-semibold">Customer</th>
              <th className="px-3 py-3 text-left font-semibold">Status</th>
              <th className="px-3 py-3 text-right font-semibold">Total</th>
              <th className="px-6 py-3 text-right font-semibold">Placed</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o, i) => {
              const pill = ORDER_STATUS_PILL[o.status];
              return (
                <tr
                  key={o.id}
                  className={
                    "transition-colors hover:bg-paper-warm " +
                    (i < orders.length - 1 ? "border-b border-ink-100" : "")
                  }
                >
                  <td className="px-6 py-3">
                    <Link
                      to={`/orders/${o.id}`}
                      className="font-medium text-ink-900 tabular-nums hover:underline"
                    >
                      #{o.id.slice(0, 8)}
                    </Link>
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-ink-900">
                      {o.customer_company_name ?? o.customer_email ?? "—"}
                    </div>
                    {o.customer_company_name && o.customer_email ? (
                      <div className="text-caption text-ink-500">{o.customer_email}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-3">
                    <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
                  </td>
                  <td className="px-3 py-3 text-right font-serif text-title-sm text-ink-900 tabular-nums">
                    {formatGBP(o.total_minor)}
                  </td>
                  <td className="px-6 py-3 text-right text-ink-500 tabular-nums">
                    {formatPlacedAt(o.placed_at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/* ─── Pending applications card ───────────────────────────────────────── */

function PendingApplicationsCard({
  applications,
  totalPending,
}: {
  applications: AdminApplicationSummary[];
  totalPending: number;
}) {
  return (
    <Card as="section" className="flex flex-col overflow-hidden">
      <header className="flex items-center justify-between gap-4 border-b border-ink-200 bg-paper-warm px-5 py-3.5">
        <h3 className="font-serif text-title-md text-ink-900">Pending applications</h3>
        {totalPending > 0 ? (
          <StatusPill tone="warning">
            {totalPending} waiting
          </StatusPill>
        ) : null}
      </header>

      {applications.length === 0 ? (
        <div className="px-5 py-6 text-body-sm text-ink-500">No applications waiting for review.</div>
      ) : (
        <ul className="divide-y divide-ink-100">
          {applications.map((a) => (
            <li key={a.id} className="flex items-center gap-3 px-5 py-3">
              <div
                aria-hidden
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-accent-soft font-serif text-body-sm font-semibold text-accent"
              >
                {a.business_legal_name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-body-sm font-medium text-ink-900">
                  {a.business_legal_name}
                </div>
                <div className="text-caption text-ink-500">
                  {BUSINESS_TYPE_LABEL[a.business_type] ?? a.business_type} · {a.user_email}
                </div>
              </div>
              <Link
                to={`/applications/${a.id}`}
                className="shrink-0 text-body-sm font-medium text-accent hover:underline"
              >
                Review
              </Link>
            </li>
          ))}
        </ul>
      )}

      {totalPending > applications.length ? (
        <footer className="border-t border-ink-100 px-5 py-3 text-center">
          <Link
            to="/applications"
            className="text-body-sm font-medium text-accent hover:underline"
          >
            See all {totalPending} →
          </Link>
        </footer>
      ) : null}
    </Card>
  );
}

/* ─── Below stock floor / refunds card ────────────────────────────────── */

function BelowStockFloorCard({
  lowStockCount,
  pendingRefundsCount,
}: {
  lowStockCount: number;
  pendingRefundsCount: number;
}) {
  return (
    <Card as="section" className="flex flex-col gap-4 p-5">
      <header className="flex items-baseline justify-between gap-4">
        <h3 className="font-serif text-title-md text-ink-900">Inventory & refunds</h3>
      </header>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-body-sm text-ink-900">Below stock floor</div>
            <div className="text-caption text-ink-500">
              {lowStockCount === 0
                ? "All SKUs above floor."
                : `${lowStockCount} variant${lowStockCount === 1 ? "" : "s"} at or below 5 in stock.`}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={
                "font-serif text-title-md tabular-nums " +
                (lowStockCount > 0 ? "text-warning" : "text-ink-500")
              }
            >
              {lowStockCount}
            </span>
            {lowStockCount > 0 ? (
              <Link to="/products" className="text-body-sm font-medium text-accent hover:underline">
                Restock →
              </Link>
            ) : null}
          </div>
        </div>

        <div className="border-t border-ink-100 pt-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-body-sm text-ink-900">Pending refunds</div>
              <div className="text-caption text-ink-500">
                {pendingRefundsCount === 0
                  ? "No refunds in flight."
                  : `${pendingRefundsCount} refund${pendingRefundsCount === 1 ? "" : "s"} awaiting processor settlement.`}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={
                  "font-serif text-title-md tabular-nums " +
                  (pendingRefundsCount > 0 ? "text-danger" : "text-ink-500")
                }
              >
                {pendingRefundsCount}
              </span>
              {pendingRefundsCount > 0 ? (
                <Link to="/orders" className="text-body-sm font-medium text-accent hover:underline">
                  Investigate →
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

/* ─── Range toggle (unchanged from P08-T9) ────────────────────────────── */

function RangeToggle({ value, onChange }: { value: Range; onChange: (next: Range) => void }) {
  return (
    <div
      role="radiogroup"
      aria-label="Revenue trend range"
      className="inline-flex rounded-sm border border-ink-200 bg-white text-caption font-medium shadow-1"
    >
      {([7, 30] as const).map((r) => {
        const selected = value === r;
        return (
          <button
            key={r}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(r)}
            className={
              "px-3 py-1.5 outline-none focus-visible:shadow-focus first:rounded-l-sm last:rounded-r-sm transition-colors duration-fast " +
              (selected ? "bg-accent text-paper-warm" : "text-ink-500 hover:bg-paper-warm")
            }
          >
            {r}d
          </button>
        );
      })}
    </div>
  );
}

/* ─── Skeleton ────────────────────────────────────────────────────────── */

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-8" aria-busy="true" aria-label="Loading dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-8 w-32" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <Skeleton className="h-80 lg:col-span-2" />
        <div className="flex flex-col gap-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-40" />
        </div>
      </div>
    </div>
  );
}
